import { useCallback, useEffect, useRef } from 'react';
import { apiRequest } from '../config/api';

export function useOAuthPopup({ onSuccess, onError }) {
  const popupRef = useRef(null);
  const handlerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (handlerRef.current) {
        window.removeEventListener('message', handlerRef.current);
        handlerRef.current = null;
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  const connect = useCallback(async (provider) => {
    try {
      const resp = await apiRequest(`/connections/${provider}/authorize`);
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        onError?.(err.detail || 'Failed to start connection');
        return;
      }
      const data = await resp.json();

      const popup = window.open(data.auth_url, 'oauth_popup', 'width=600,height=700,scrollbars=yes');

      if (!popup) {
        onError?.('Popup was blocked. Please allow popups for this site.');
        return;
      }

      popupRef.current = popup;

      // Remove any previous listener
      if (handlerRef.current) {
        window.removeEventListener('message', handlerRef.current);
      }

      const expectedOrigin = window.location.origin;

      const handler = (event) => {
        // Validate origin — only accept messages from our backend
        if (event.origin !== expectedOrigin && event.origin !== new URL(data.auth_url).origin) {
          // Also accept from the backend URL (callback page is served by backend)
          const backendOrigin = new URL(resp.url).origin;
          if (event.origin !== backendOrigin) return;
        }

        if (event.data?.type === 'oauth_complete') {
          window.removeEventListener('message', handler);
          handlerRef.current = null;
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }
          popupRef.current = null;
          if (event.data.success) {
            onSuccess?.(event.data);
          } else {
            onError?.(event.data.error || 'Connection failed');
          }
        }
      };

      handlerRef.current = handler;
      window.addEventListener('message', handler);

      // Timeout after 5 minutes — cleanup
      setTimeout(() => {
        if (handlerRef.current === handler) {
          window.removeEventListener('message', handler);
          handlerRef.current = null;
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }
          popupRef.current = null;
        }
      }, 5 * 60 * 1000);

    } catch (err) {
      onError?.(err.message || 'Failed to start connection');
    }
  }, [onSuccess, onError]);

  return { connect };
}
